const API_HOST =
  process.env.REACT_APP_API_HOST ||
  (window as Window & { __API_HOST__?: string }).__API_HOST__ ||
  '';

const API_BASE = `${API_HOST}/api/robinhood/deep`;

export const ROBINHOOD_SCALAR_FILTER_KEYS = [
  'min_winrate', 'max_winrate',
  'min_roi', 'max_roi',
  'min_pnl_usd', 'max_pnl_usd',
  'min_spent_usd', 'max_spent_usd',
  'min_earned_usd', 'max_earned_usd',
  'min_all_tokens_spent_usd', 'max_all_tokens_spent_usd',
  'min_tokens_without_sells_spent_usd', 'max_tokens_without_sells_spent_usd',
  'min_avg_token_buys', 'max_avg_token_buys',
  'min_avg_token_sells', 'max_avg_token_sells',
  'min_avg_avg_sell_percent', 'max_avg_avg_sell_percent',
  'min_median_avg_token_sell_percent', 'max_median_avg_token_sell_percent',
  'min_avg_token_holding_percent', 'max_avg_token_holding_percent',
  'min_not_closed_positions_tokens_percent', 'max_not_closed_positions_tokens_percent',
  'min_total_traded_tokens', 'max_total_traded_tokens',
  'min_tokens_with_sells_count', 'max_tokens_with_sells_count',
  'min_tokens_without_sells_count', 'max_tokens_without_sells_count',
  'min_tokens_without_sells_percent', 'max_tokens_without_sells_percent',
  'min_total_buys_count', 'max_total_buys_count',
  'min_total_sells_count', 'max_total_sells_count',
  'min_total_swaps_count', 'max_total_swaps_count',
  'min_median_token_roi', 'max_median_token_roi',
  'min_avg_token_roi', 'max_avg_token_roi',
  'min_avg_first_mcap_usd', 'max_avg_first_mcap_usd',
  'min_avg_last_mcap_usd', 'max_avg_last_mcap_usd',
  'min_median_first_mcap_usd', 'max_median_first_mcap_usd',
  'min_median_last_mcap_usd', 'max_median_last_mcap_usd',
  'min_median_token_trade_duration_seconds', 'max_median_token_trade_duration_seconds',
  'min_avg_token_trade_duration_seconds', 'max_avg_token_trade_duration_seconds',
  'min_avg_token_seconds_first_sell_duration', 'max_avg_token_seconds_first_sell_duration',
  'min_sold_before_buy_tokens_percent', 'max_sold_before_buy_tokens_percent',
  'min_sold_more_than_bought_tokens_percent', 'max_sold_more_than_bought_tokens_percent',
  'min_router_swaps_count', 'max_router_swaps_count',
  'min_source_events_count', 'max_source_events_count',
  'min_multi_event_swaps_token_percent', 'max_multi_event_swaps_token_percent',
  'min_rocket_x2_count', 'max_rocket_x2_count',
  'min_rocket_x5_count', 'max_rocket_x5_count',
  'min_rocket_x10_count', 'max_rocket_x10_count',
  'last_activity_max_age_seconds', 'last_activity_min_age_seconds',
  'wallet_age_min_seconds', 'wallet_age_max_seconds',
] as const;

export type RobinhoodScalarFilterKey = typeof ROBINHOOD_SCALAR_FILTER_KEYS[number];
export type RobinhoodMapName = 'quote_tokens' | 'projects' | 'trade_providers';
export type RobinhoodMetricMap = Record<string, { min: number | null; max: number | null }>;

export type CustomBalanceFilter = {
  contract: `0x${string}`;
  min: string | null;
  max: string | null;
};

export type BalanceFilters = {
  weth_balance_min: string | null;
  weth_balance_max: string | null;
  eth_balance_min: string | null;
  eth_balance_max: string | null;
  total_eth_balance_min: string | null;
  total_eth_balance_max: string | null;
  usdg_balance_min: string | null;
  usdg_balance_max: string | null;
  custom_balance: CustomBalanceFilter[];
  balance_mode_or: boolean;
};

export type RobinhoodFilters = Partial<Record<RobinhoodScalarFilterKey, number | null>> & BalanceFilters & {
  quote_tokens: RobinhoodMetricMap;
  projects: RobinhoodMetricMap;
  trade_providers: RobinhoodMetricMap;
};

export type RobinhoodPeriod = 1 | 7 | 14 | 30 | 45 | 90;
export type RobinhoodFormat = 'EXCEL' | 'TXT_EXCEL' | 'TXT';

export interface RobinhoodPreset {
  id: number;
  name: string;
  is_active: boolean;
  is_active_preset: boolean;
  period_days: RobinhoodPeriod;
  format_type: RobinhoodFormat;
  include_token_details: boolean;
  tokens_per_wallet: number;
  filters: RobinhoodFilters;
  summary_order_column: string;
  summary_order_desc: boolean;
  token_order_column: string;
  token_order_desc: boolean;
}

export type RobinhoodPresetPayload = Omit<RobinhoodPreset, 'id' | 'filters'> & {
  filters: Omit<RobinhoodFilters, keyof BalanceFilters> & {
    weth_balance_min: string | null;
    weth_balance_max: string | null;
    eth_balance_min: string | null;
    eth_balance_max: string | null;
    total_eth_balance_min: string | null;
    total_eth_balance_max: string | null;
    usdg_balance_min: string | null;
    usdg_balance_max: string | null;
    custom_balance: CustomBalanceFilter[];
    balance_mode_or: boolean;
  };
};

export interface RobinhoodExportCreated {
  task_id: string;
  status: string;
  queued_for_release: boolean;
  preset: string;
  limit: number;
}

export interface RobinhoodExportTask {
  task_id: string;
  status: 'created' | 'queued_release' | 'analyzing_wallets' | 'loading_tokens' | 'building_files' | 'done' | 'error' | 'canceled';
  total_rows: number;
  processed_rows: number;
  matched_rows: number;
  result_sended: boolean;
  exception: string | null;
  queued_for_release: boolean;
}

export interface RobinhoodWinrateBucket {
  wallets_count: number;
  percent: number;
  winrate_percent: number;
}

const getInitData = () => {
  const telegramWindow = window as Window & {
    Telegram?: { WebApp?: { initData?: string } };
  };
  return telegramWindow.Telegram?.WebApp?.initData || '';
};

const headers = (json = false): HeadersInit => ({
  'Init-Data': getInitData(),
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

async function readResponse<T>(response: Response): Promise<T> {
  if (response.ok) {
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }

  let message = `Request failed (${response.status})`;
  try {
    const body = await response.json();
    if (typeof body?.detail === 'string') {
      message = body.detail;
    } else if (Array.isArray(body?.detail)) {
      message = body.detail
        .map((item: { loc?: Array<string | number>; msg?: string }) =>
          `${item.loc?.slice(1).join('.') || 'Field'}: ${item.msg || 'invalid value'}`)
        .join('; ');
    } else if (response.status === 401) {
      message = 'Open this page inside Telegram to authorize.';
    }
  } catch {
    // Keep the HTTP fallback when the server returns a non-JSON error.
  }
  throw new Error(message);
}

export const robinhoodPresetsClient = {
  async getWinrateSpectrum(periodDays: RobinhoodPeriod, signal?: AbortSignal): Promise<RobinhoodWinrateBucket[]> {
    const response = await fetch(`${API_BASE}/stat/winrate?period_days=${periodDays}`, { headers: headers(), signal });
    return readResponse<RobinhoodWinrateBucket[]>(response);
  },

  async getPresets(): Promise<RobinhoodPreset[]> {
    const response = await fetch(`${API_BASE}/presets`, { headers: headers() });
    return readResponse<RobinhoodPreset[]>(response);
  },

  async getPreset(id: number): Promise<RobinhoodPreset> {
    const response = await fetch(`${API_BASE}/presets/${id}`, { headers: headers() });
    return readResponse<RobinhoodPreset>(response);
  },

  async createPreset(payload: Partial<RobinhoodPresetPayload> & { name: string }): Promise<RobinhoodPreset> {
    const response = await fetch(`${API_BASE}/presets`, {
      method: 'POST',
      headers: headers(true),
      body: JSON.stringify(payload),
    });
    return readResponse<RobinhoodPreset>(response);
  },

  async updatePreset(id: number, payload: RobinhoodPresetPayload): Promise<RobinhoodPreset> {
    const response = await fetch(`${API_BASE}/presets/${id}`, {
      method: 'PUT',
      headers: headers(true),
      body: JSON.stringify(payload),
    });
    return readResponse<RobinhoodPreset>(response);
  },

  async deletePreset(id: number): Promise<void> {
    const response = await fetch(`${API_BASE}/presets/${id}`, {
      method: 'DELETE',
      headers: headers(),
    });
    return readResponse<void>(response);
  },

  async exportWallets(id: number): Promise<RobinhoodExportCreated> {
    const response = await fetch(`${API_BASE}/presets/${id}/export_wallets`, {
      method: 'POST',
      headers: headers(),
    });
    return readResponse<RobinhoodExportCreated>(response);
  },

  async getExportTask(taskId: string): Promise<RobinhoodExportTask> {
    const response = await fetch(`${API_BASE}/exports/${encodeURIComponent(taskId)}`, {
      headers: headers(),
    });
    return readResponse<RobinhoodExportTask>(response);
  },
};
